import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Icon, Modal } from "semantic-ui-react";

import { CUSTOM_CONFIRM_TYPES, CustomConfirm } from "components";
import { createFormHelper } from "lib/Form";
import { CreateDealerForm } from "modules/DealersDashboard/components/CreateDealerModal/components";
import "modules/DealersDashboard/components/CreateDealerModal/CreateDealerModal.scss";
import { useCreateDealerMutation } from "modules/DealersDashboard/components/CreateDealerModal/hooks";

import type { DealerFormPayload } from "modules/DealersDashboard/components/CreateDealerModal/hooks";

const CREATE_DEALER_FORM_ID = "create-dealer-form";
const FormHelper = createFormHelper<DealerFormPayload>();

type CreateDealerModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const CreateDealerModal = ({ isOpen, onClose }: CreateDealerModalProps) => {
  const { t } = useTranslation();
  const formMethods = FormHelper.useForm({
    defaultValues: { name: "", active: true, region: "", city: "", street: "", postal_code: "", phone_nr: "", website_url: "" },
    mode: "all"
  });

  const [dealerToCreate, setDealerToCreate] = useState<DealerFormPayload | null>(null);
  const { mutate: createDealer, isPending: isCreateDealerPending } = useCreateDealerMutation();

  const handleClose = () => {
    formMethods.reset();
    onClose();
  };

  const handleSubmit = (values: DealerFormPayload) => {
    if (!values.name) return;

    setDealerToCreate(values);
  };

  const handleConfirm = () => {
    if (!dealerToCreate) return;

    createDealer(dealerToCreate, {
      onSuccess: () => {
        setDealerToCreate(null);
        handleClose();
      }
    });
  };

  return (
    <>
      <Modal open={isOpen} size="small" closeOnDimmerClick={false} onClose={handleClose} className="CreateDealerModal">
        <FormHelper.FormProvider {...formMethods}>
          <Modal.Header>
            <div className="header-container">
              <div>{t("v8_add_dealer", "Add dealer")}</div>
              <FormHelper.Field.Checkbox name="active" label={t("v8_active", "Active")} />
            </div>
          </Modal.Header>
          <Modal.Content>
            <Form id={CREATE_DEALER_FORM_ID} onSubmit={formMethods.handleSubmit(handleSubmit)} disabled={isCreateDealerPending}>
              <CreateDealerForm />
            </Form>
          </Modal.Content>
          <Modal.Actions>
            <div className="action-buttons-container">
              <Button className="light" type="button" onClick={handleClose} disabled={isCreateDealerPending}>
                {t("v8_cancel", "Cancel")}
                <Icon className="xmark" />
              </Button>
              <Button type="submit" form={CREATE_DEALER_FORM_ID} color="green" disabled={isCreateDealerPending} loading={isCreateDealerPending}>
                {t("v8_save", "Save")}
                <Icon className="check" />
              </Button>
            </div>
          </Modal.Actions>
        </FormHelper.FormProvider>
      </Modal>
      <CustomConfirm
        hideCancelButton
        type={CUSTOM_CONFIRM_TYPES.Warning}
        isOpen={!!dealerToCreate}
        isLoading={isCreateDealerPending}
        confirmMsg={t("v8_create_dpo_reminder", "You need to assign a DPO for this dealer.")}
        handleCancel={() => setDealerToCreate(null)}
        handleConfirm={handleConfirm}
      />
    </>
  );
};
